// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : help wiki templates - see ./wiki_templates.md
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Icon, Label} from 'semantic-ui-react'
import {JSONTree} from 'uix/chunks'

// ---Framework imports
import {mapItems} from 'ldash'
import log from 'uix/logging'
import {Actions, Sel} from 'uix/registries'
import {decodeAttribH5} from 'uix/utils'
import {GenericTagHandler, TagMatcher} from 'uix/wiki/utils'
import {useActions} from 'uix/hooks'
import {checkAcl} from 'uix/auth/acl'

// ---Exports

export const UserRole = {
  match: ({tag}) => tag == 'UserRole',
  build: ({attrib, data}) => {
    attrib = decodeAttribH5(attrib) // understand html5 shortforms for booleans

    let text = '?'
    if (data.user.groups.includes('admin')) {
      text = 'Administrator'
    } else {
      text = 'QS'
    }

    if (attrib.label) {
      return (
        <Label>
          <Icon name="user" /> {text}
        </Label>
      )
    } else {
      return <span>{text}</span>
    }
  },
  completion: () => ({
    '!top': ['UserRole'],
    UserRole: {
      attrs: {
        label: ['true', 'false'], // true: fancy formatting
      },
    },
  }),
  tagMatcher: 'UserRole',
  docs: 'Display a Userrole.',
}

export const Help = TagMatcher(
  'Help',
  ({text, data, id, wikiIdentifier}) => {
    let navigateHelp = useActions(Actions.navigateHelp)
    return (
      <span
        style={{cursor: 'pointer', color: 'blue'}}
        onClick={() => navigateHelp(wikiIdentifier, `help/${id}`)}
      >
        {text || id}
      </span>
    )
  },
  {
    id: [''],
  },
  'Link to another help section.'
)

export const VLVersion = TagMatcher(
  'VLVersion',
  ({attrib, iconfig}) => (
    <Label ribbon={attrib.ribbon}>
      <Icon name="vuejs" /> {iconfig.info.version.toUpperCase()}
    </Label>
  ),
  {
    ribbon: ['true', 'false'],
  },
  'How VLS Version/Branch.'
)

export const Acl = GenericTagHandler(
  /* client-side rendering
   */
  'Acl',
  ({attrib, applyTemplates, data}) => {
    let acls = mapItems(attrib, (name, value) => {
      // undo html5 parser mangling of '-' and ':'
      // g:debug => g_debug
      // -g:admin => _g_admin
      let prefix = ''
      if (name.startsWith('_')) {
        prefix = '-'
        name = name.slice(1)
      }
      if (name[1] !== '_') {
        return null
      } else {
        return `${prefix}${name[0]}:${name.slice(2)}`
      }
    }).filter(e => e)

    if (checkAcl(acls, data.user, data.dname)) {
      return applyTemplates()
    }
    return null
  },
  {
    'g:admin': '',
  },
  'Do not render children if user has not the required group memberships.'
)

export const DebugGenericTagHandler = GenericTagHandler(
  'DebugGenericTagHandler',
  props => <JSONTree hideRoot={true} data={props} />,
  'This Element renders all props for a DebugGenericTagHandler implementation.'
)

export const DebugTagMatcher = TagMatcher(
  'DebugTagMatcher',
  props => <JSONTree hideRoot={true} data={props} />,
  `This Element renders all props for a TagMatcher implementation.
  A TagMatcher creates a React component with direct attrib -> props mapping.`
)
